import { BarChartOutlined, TableOutlined } from '@ant-design/icons';

import { IMetricCellConfig } from './index';

import { IDonorPipelineStats } from '../../../../hooks/useDonorPipelineStats';
import { IRecruiterDashboardMetrics } from '../../../../hooks/useRecruiterDashboardMetrics';
import { STATUS_COLORS } from '../../constants';
import { TrendDirection } from '../../types';
import { TabLinkButton } from '../TabLinkButton';

interface IPipelineTrends {
  retentionTrend: TrendDirection;
  retentionTrendColor: string;
  dropoutTrend: TrendDirection;
  dropoutTrendColor: string;
  ratioTrend: TrendDirection;
  ratioTrendColor: string;
}

interface IPipelineCellHandlers {
  onActiveDonorsClick: () => void;
  onPendingDonorsClick: () => void;
  onApprovedDonorsClick: () => void;
  onRetentionDetailsClick: () => void;
  onDropoutDetailsClick: () => void;
  onRatioDetailsClick: () => void;
}

interface IBuildPipelineMetricsCellsProps {
  pipelineStats: IDonorPipelineStats | null;
  pipelineLoading: boolean;
  dashboardMetrics: IRecruiterDashboardMetrics | null;
  metricsLoading: boolean;
  trends: IPipelineTrends;
  contentWidth: number;
  handlers: IPipelineCellHandlers;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export function buildPipelineMetricsCells({
  pipelineStats,
  pipelineLoading,
  dashboardMetrics,
  metricsLoading,
  trends,
  contentWidth,
  handlers,
  t,
}: IBuildPipelineMetricsCellsProps): IMetricCellConfig[] {
  const {
    retentionTrend,
    retentionTrendColor,
    dropoutTrend,
    dropoutTrendColor,
    ratioTrend,
    ratioTrendColor,
  } = trends;
  const {
    onActiveDonorsClick,
    onPendingDonorsClick,
    onApprovedDonorsClick,
    onRetentionDetailsClick,
    onDropoutDetailsClick,
    onRatioDetailsClick,
  } = handlers;

  return [
    {
      label: t('totalActiveDonors'),
      value: (pipelineStats?.activeDonorCount ?? 0).toLocaleString(),
      loading: pipelineLoading,
      action: (
        <TabLinkButton
          onClick={onActiveDonorsClick}
          fontSize={10}
          iconColor={STATUS_COLORS.good.fill}
          icon={<TableOutlined />}
          label={t('donorListLabel')}
          tooltip={t('activeDonorsTooltip')}
          windowWidth={contentWidth}
        />
      ),
    },
    {
      label: t('totalPendingDonors'),
      value: (pipelineStats?.pendingDonorCount ?? 0).toLocaleString(),
      loading: pipelineLoading,
      action: (
        <TabLinkButton
          onClick={onPendingDonorsClick}
          fontSize={10}
          iconColor={STATUS_COLORS.ok.fill}
          icon={<TableOutlined />}
          label={t('donorListLabel')}
          tooltip={t('pendingDonorsTooltip')}
          windowWidth={contentWidth}
        />
      ),
    },
    {
      label: t('totalApprovedDonors'),
      value: (pipelineStats?.approvedDonorCount ?? 0).toLocaleString(),
      loading: pipelineLoading,
      action: (
        <TabLinkButton
          onClick={onApprovedDonorsClick}
          fontSize={10}
          iconColor={STATUS_COLORS.ok.fill}
          icon={<TableOutlined />}
          label={t('donorListLabel')}
          tooltip={t('approvedDonorsTooltip')}
          windowWidth={contentWidth}
        />
      ),
    },
    {
      label: t('retentionScoreLabel'),
      value: `${Math.round(dashboardMetrics?.retentionScore ?? 0)}%`,
      loading: metricsLoading,
      trend: {
        direction: retentionTrend,
        color: retentionTrendColor,
        label: retentionTrend.toUpperCase(),
      },
      action: (
        <TabLinkButton
          onClick={onRetentionDetailsClick}
          fontSize={10}
          iconColor={retentionTrendColor}
          icon={<BarChartOutlined />}
          label={t('retentionDetailsLabel')}
          tooltip={t('retentionDetailsTooltip')}
          windowWidth={contentWidth}
        />
      ),
    },
    {
      label: t('dropoutRateLabel'),
      value: `${Math.round(dashboardMetrics?.quarterlyDropoutRate ?? 0)}%`,
      loading: metricsLoading,
      trend: {
        direction: dropoutTrend,
        color: dropoutTrendColor,
        label: dropoutTrend.toUpperCase(),
      },
      action: (
        <TabLinkButton
          onClick={onDropoutDetailsClick}
          fontSize={10}
          iconColor={dropoutTrendColor}
          icon={<BarChartOutlined />}
          label={t('dropoutDetailsLabel')}
          tooltip={t('dropoutDetailsTooltip')}
          windowWidth={contentWidth}
        />
      ),
    },
    {
      label: t('ratioLabel'),
      value: `${dashboardMetrics?.ratioFirstTime ?? 0} : ${
        dashboardMetrics?.ratioRepeat ?? 0
      }`,
      loading: metricsLoading,
      trend: {
        direction: ratioTrend,
        color: ratioTrendColor,
        label: ratioTrend.toUpperCase(),
      },
      action: (
        <TabLinkButton
          onClick={onRatioDetailsClick}
          fontSize={10}
          iconColor={ratioTrendColor}
          icon={<BarChartOutlined />}
          label={t('ratioDetailsLabel')}
          tooltip={t('ratioDetailsTooltip')}
          windowWidth={contentWidth}
        />
      ),
    },
  ];
}
